import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import { BaseModal } from 'components/UI';
import { Formik, Field } from 'formik';
import useStyle from './RulesModal.styled';
import { useDispatch, useSelector } from 'react-redux';
import { ruleSchema } from './RulesSchema';
import { MaterialSelectField } from 'components/FormFields';
import { Box, Grid, Typography, Button } from '@material-ui/core';
import { capitalize, buildUrl, parseInputNumber } from 'utils';
import { PricingActions } from 'state/modules/pricing';
import { InvoiceTypeSelect, PricingTypesSelect } from 'components/AsyncFields';
import AddIcon from '@material-ui/icons/AddCircle';
import { RateInput } from './RateInput';

const RulesModal = ({ open, index, handleClick }) => {
  const classes = useStyle();
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const [requirementTypeError, setRequirementTypeError] = useState('');
  const rules = useSelector((state) => state.pricing?.current?.rules || []);
  const rule = rules && (index || index === 0) ? rules[index] : {};
  const [units, setUnits] = useState(rule?.units || {});
  const [accountingType, setAccountingType] = useState(rule?.invoice_item_type);
  const [combosAllowed, setCombosAllowed] = useState(
    rule?.combos_allowed ?? rule?.invoice_item_type?.combos_allowed ?? true
  );

  const [name, setName] = useState(rule?.invoice_item_type?.name || '');
  const [useAdditionalMiles, setAdditionalMiles] = useState(
    !!rule?.extra_arguments?.distance_allowance
  );
  const [SKUDescription, setSKUDescription] = useState(rule?.description || '');
  const allowAdditionalMilesEnabled = useSelector(
    (state) => state.config?.settings?.core?.pricing?.allow_additional_miles || false
  );
  const defaultRoundingType = useSelector(
    (state) => state.config?.settings?.core?.pricing?.default_rounding_type || null
  );

  const initialValues = {
    pricing_type: rule?.invoice_item_type?.pricing_type || '',
    invoice_item_type_id: rule?.invoice_item_type?.id || '',
    category: rule?.category || '',
    description: rule?.description || '',
    start: rule?.start === 1 && rule?.end === 8 ? '' : rule?.start || '',
    end: rule?.end || 8,
    end_week: !rule?.end || rule?.end === 8 ? true : false,
    start_day:
      rule?.start === 1 && rule?.end === 8 ? '' : rule?.start ? Math.floor(rule?.start) : '',
    end_day: rule?.start === 1 && rule?.end === 8 ? '' : rule?.end ? Math.floor(rule?.end) : '',
    all_time: rule?.invoice_item_type?.units ? rule?.start === 1 && rule?.end === 8 : true,
    unit_price: typeof rule?.unit_price === 'number' ? rule?.unit_price / 100 : '',
    rounding_strategy: rule?.rounding_strategy || '',
    extra_arguments: {
      financing_factor: rule?.extra_arguments?.financing_factor || '',
      max_time_to_bill: rule?.extra_arguments?.max_time_to_bill || '',
      grace_period: rule?.extra_arguments?.grace_period || '',
      out_of_state: rule?.extra_arguments?.out_of_state || '',
      level_pct: rule?.extra_arguments?.level_pct || '',
      operator: rule?.extra_arguments?.operator || '',
      tiers: rule?.extra_arguments?.tiers || {},
      ...(allowAdditionalMilesEnabled && !!rule?.extra_arguments?.distance_allowance
        ? {
            distance_allowance: rule?.extra_arguments?.distance_allowance || 0,
            timeframe: rule?.extra_arguments?.timeframe || '',
            acceptable_excess: rule?.extra_arguments?.acceptable_excess || 0,
          }
        : {}),
    },
  };

  const formSchema = ruleSchema(rule, allowAdditionalMilesEnabled);

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  const handleSubmit = (values) => {
    let data = { ...values };
    if (values?.pricing_type?.categories_allowed?.length && !values?.category) {
      setRequirementTypeError(i18n.t('common.required_field'));
      return false;
    }
    setRequirementTypeError('');

    data.description = SKUDescription;
    data.invoice_item_type = {
      units: units,
      name: name,
      id: data?.invoice_item_type_id,
      is_revenue: accountingType?.is_revenue ? true : false,
      is_hold: accountingType?._type === 'HOLD' ? true : false,
      pricing_type: data?.pricing_type || {},
    };

    delete data.start_day;
    delete data.end_day;
    delete data.all_time;

    if (data.pricing_type.name === 'DISTANCEDRIVEN') {
      if (allowAdditionalMilesEnabled && useAdditionalMiles) {
        data.extra_arguments = { ...data.extra_arguments, operator: 'greater_than' };
        if (
          !data.extra_arguments.distance_allowance === '' ||
          data.extra_arguments.distance_allowance <= 0
        ) {
          data.extra_arguments.distance_allowance = 0;
          data.extra_arguments.acceptable_excess = 0;
        }
        if (
          data.extra_arguments.distance_allowance &&
          (data.extra_arguments.acceptable_excess <= 0 ||
            data.extra_arguments.acceptable_excess === '')
        ) {
          data.extra_arguments.acceptable_excess = 0;
        }
        if (!data.extra_arguments.timeframe) {
          data.extra_arguments.timeframe = 'hourly';
        }
        data.extra_arguments.distance_allowance = parseInt(data.extra_arguments.distance_allowance);
        data.extra_arguments.acceptable_excess = parseInt(data.extra_arguments.acceptable_excess);
      } else {
        data.extra_arguments = {};
      }
    }

    if (!data.start) {
      data.start = 1;
    }

    if (!data.end) {
      data.end = 8;
    }

    if ((index || index === 0) && data.unit_price) {
      data.unit_price = parseInputNumber(data.unit_price);
    }

    if (
      !data.rounding_strategy &&
      ['BILLABLETIME', 'ADDITIONALTIMEUSEDFEE'].includes(data.pricing_type.name)
    ) {
      data.rounding_strategy = defaultRoundingType || 'ROUND';
    }

    if (!rule || !Object.keys(rule).length) {
      dispatch(PricingActions.fetchAddRuleset(data));
    } else {
      dispatch(PricingActions.fetchUpdateRuleById(index, data));
    }

    handleClick();
  };

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      handleClick={handleClick}
      enterButtonLabel={index || index === 0 ? i18n.t('common.update') : i18n.t('common.save')}
      title={index || index === 0 ? i18n.t('skus.update_information') : i18n.t('common.create')}
    >
      <Grid container className={classes.container}>
        <Formik
          initialValues={initialValues}
          validationSchema={formSchema}
          enableReinitialize={true}
          innerRef={formRef}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, errors, values, setFieldValue }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid item xs={12} className={classes.formContainer}>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    <Box fontSize={13} style={{ height: '100%' }}>
                      {i18n.t('ruleset.sku_description')}
                    </Box>
                  </Typography>
                </Grid>

                <Grid item xs={12} className={classes.mt}>
                  <Typography variant="h6">
                    <Box
                      fontWeight="fontWeightBold"
                      fontSize={14}
                      mb={2}
                      style={{ height: '100%' }}
                    >
                      {i18n.t('ruleset.kind_rule')}
                    </Box>
                  </Typography>
                  <Grid
                    container
                    spacing={1}
                    style={{
                      justifyContent: 'space-between',
                    }}
                  >
                    <Grid item md={6} xs={6}>
                      <Field
                        errors={errors}
                        name="pricing_type"
                        multiple={false}
                        margin="none"
                        label={i18n.t('skus.wizard.type')}
                        customParams={{
                          group_type: 'VEHICLE',
                          auto: true,
                        }}
                        setFieldValue={setFieldValue}
                        component={PricingTypesSelect}
                      />
                    </Grid>
                    {values.pricing_type !== '' && values.pricing_type?.length !== 0 && (
                      <Grid item xs={6}>
                        <Field
                          margin="none"
                          errors={errors}
                          id="ad-hoc-select"
                          name="invoice_item_type_id"
                          component={InvoiceTypeSelect}
                          setFieldValue={setFieldValue}
                          label={i18n.t('ruleset.sku_name')}
                          onChange={(val) => {
                            setUnits(val?.units);
                            setAccountingType(val?.accounting_type);
                            setCombosAllowed(val?.combos_allowed);
                            setName(val?.name);
                            setSKUDescription(val?.description);
                          }}
                          customParams={{
                            auto: true,
                            pricing_type: values.pricing_type?.name,
                            is_account_level: false,
                          }}
                        />
                        <Button
                          color="primary"
                          fontSize={13}
                          style={{
                            textTransform: 'capitalize',
                            marginRight: 15,
                          }}
                          href={buildUrl('/skus/create')}
                          target="_blank"
                          startIcon={<AddIcon />}
                        >
                          {i18n.t('skus.create_sku')}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                {values.pricing_type !== '' && values.pricing_type?.length !== 0 && (
                  <>
                    {values?.pricing_type?.categories_allowed?.length ? (
                      <Grid item xs={12} className={classes.mtLg}>
                        <Typography variant="h6">
                          <Box fontWeight="fontWeightBold" fontSize={13}>
                            {i18n.t('ruleset.requirement_type')}
                          </Box>
                        </Typography>

                        <Grid item md={6}>
                          <Field
                            errors={errors}
                            name="category"
                            margin="none"
                            asyncErrors={requirementTypeError}
                            component={MaterialSelectField}
                            options={
                              values?.pricing_type?.categories_allowed?.map((val) => {
                                return {
                                  value: val,
                                  label: capitalize(val?.toLowerCase()),
                                };
                              }) || []
                            }
                            label={i18n.t('ruleset.requirement_type')}
                            placeholder={i18n.t('ruleset.requirement_type')}
                            style={{ marginTop: 30 }}
                          />
                        </Grid>
                      </Grid>
                    ) : (
                      ''
                    )}
                  </>
                )}
                <RateInput
                  values={values}
                  units={units}
                  formRef={formRef}
                  setFieldValue={setFieldValue}
                  combosAllowed={combosAllowed}
                  useAdditionalMiles={useAdditionalMiles}
                  setAdditionalMiles={setAdditionalMiles}
                  defaultRoundingType={defaultRoundingType}
                />
              </Grid>
            </form>
          )}
        </Formik>
      </Grid>
    </BaseModal>
  );
};

RulesModal.propTypes = {
  current: PropTypes.object,
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  index: PropTypes.number,
};

RulesModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default RulesModal;
